import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save, Send, Upload, FileUp, Lock } from 'lucide-react';
import { plansApi, PageShell, StatusBadge, CARD, FIELD, LABEL, PRIMARY, GHOST, errMsg, inr } from './shared';
import { CreatorSheetTable, emptyRow, sheetTotals } from './CreatorSheetTable';
import { PdfViewer } from './PdfViewer';
import { NotesCard, HistoryList } from './PlanSideCards';

const EDITABLE = ['draft', 'changes_requested'];

const UploadButton = ({ accept, onFile, busy, label, icon: Icon, testid }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className={GHOST} data-testid={testid}>{busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {label}</button>
    </>
  );
};

export default function AgencyPlanEditor() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [form, setForm] = useState({ campaign_name: '', agency_fee: 0, rows: [emptyRow()] });
  const [busy, setBusy] = useState(null);
  const [brandName, setBrandName] = useState('');

  const hydrate = (p) => { setPlan(p); setForm({ campaign_name: p.campaign_name, agency_fee: p.agency_fee || 0, rows: p.creator_sheet.length ? p.creator_sheet : [] }); };
  const load = useCallback(() => { if (id) plansApi.get(`/${id}`).then(hydrate).catch((e) => toast.error(errMsg(e, 'Plan not found'))); }, [id]);
  useEffect(load, [load]);
  useEffect(() => { if (!id) plansApi.get('/me').then((m) => setBrandName(m.brands.find((b) => b.id === search.get('brand'))?.brand_name || '')); }, [id, search]);

  const editable = !plan || EDITABLE.includes(plan.status);
  const body = { campaign_name: form.campaign_name, agency_fee: Number(form.agency_fee) || 0, creator_sheet: form.rows };
  const save = async (quiet) => {
    if (!plan) {
      const p = await plansApi.post('', { ...body, brand_account_id: search.get('brand') });
      nav(`/agency/plans/${p.id}`, { replace: true });
      return p;
    }
    const p = await plansApi.put(`/${plan.id}`, body);
    hydrate(p);
    if (!quiet) toast.success('Draft saved');
    return p;
  };
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(errMsg(e, 'Something went wrong')); } finally { setBusy(null); } };

  const onSave = () => run('save', async () => { const p = await save(); if (!plan) toast.success('Draft created', { description: p.campaign_name }); });
  const onSubmit = () => run('submit', async () => {
    const p = await save(true);
    hydrate(await plansApi.post(`/${p.id}/submit`));
    toast.success(plan?.status === 'changes_requested' ? 'Resubmitted for review' : 'Submitted for review');
  });
  const onCsv = (f) => run('csv', async () => {
    const { rows } = await plansApi.upload('/parse-sheet', f);
    const existing = form.rows.filter((r) => r.creator_name || r.instagram_handle);
    const replace = !existing.length || window.confirm(`Replace the ${existing.length} ${existing.length === 1 ? 'creator' : 'creators'} already in the sheet?\n\nOK = replace · Cancel = add the imported creators below them`);
    setForm((s) => ({ ...s, rows: replace ? rows : [...existing, ...rows] }));
    toast.success(`${rows.length} creators ${replace ? 'imported' : 'added'}`, { description: 'Review them below, then save.' });
  });
  const onDeck = (f) => run('deck', async () => {
    if (f.size > 25 * 1024 * 1024) { toast.error('Pitch deck must be under 25MB'); return; }
    const p = plan || await save(true);
    const { pitch_deck } = await plansApi.upload(`/${p.id}/pitch-deck`, f);
    setPlan((s) => ({ ...(s || p), pitch_deck }));
    toast.success('Pitch deck uploaded');
  });

  if (id && !plan) return <PageShell testid="agency-plan-editor"><Loader2 className="animate-spin" size={16} /></PageShell>;
  if (!id && !search.get('brand')) return <PageShell testid="agency-plan-editor"><div className={`${CARD} p-6 text-sm text-[#6B6B6B]`} data-testid="agency-plan-no-brand">Pick a brand first. <Link to="/agency/plans" className="text-[#FF451A] font-semibold">Go to your brand assignments</Link></div></PageShell>;
  const t = sheetTotals(form.rows, form.agency_fee);
  return (
    <PageShell testid="agency-plan-editor">
      <Link to="/agency/plans" className="text-xs text-[#6B6B6B] hover:text-[#FF451A] inline-flex items-center gap-1 transition-colors" data-testid="agency-plan-back"><ArrowLeft size={12} /> All plans</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[#FF451A] mb-1">{plan?.brand_name || brandName} · {plan ? `v${plan.version || 0}` : 'new submission'}</div>
          <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white" data-testid="agency-plan-title">{form.campaign_name || 'Untitled plan'}</h1>
        </div>
        <div className="flex items-center gap-2">
          {plan && <StatusBadge status={plan.status} testid="agency-plan-status" />}
          {editable && <button onClick={onSave} disabled={!!busy} className={GHOST} data-testid="agency-plan-save">{busy === 'save' ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save draft</button>}
          {editable && <button onClick={onSubmit} disabled={!!busy} className={PRIMARY} data-testid="agency-plan-submit">{busy === 'submit' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {plan?.status === 'changes_requested' ? 'Resubmit' : 'Submit for review'}</button>}
        </div>
      </div>
      {!editable && <div className={`${CARD} p-4 text-sm text-[#4B5563] dark:text-zinc-300 inline-flex items-center gap-2 w-full`} data-testid="agency-plan-locked"><Lock size={14} className="text-[#FF451A]" /> {plan.status === 'approved' ? 'Approved by the brand — this plan is locked.' : 'Under review by the brand. You can edit again if they request changes.'}</div>}
      {plan && (plan.brand_notes || plan.status === 'changes_requested') && <NotesCard notes={plan.brand_notes} status={plan.status} />}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2"><label className={LABEL}>Campaign name</label><input value={form.campaign_name} disabled={!editable} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} placeholder="e.g. Monsoon Glow launch" className={FIELD} data-testid="agency-plan-name" /></div>
        <div><label className={LABEL}>Agency fee — plan level (₹)</label><input type="number" min={0} value={form.agency_fee} disabled={!editable} onChange={(e) => setForm({ ...form, agency_fee: e.target.value })} className={FIELD} data-testid="agency-plan-fee" /></div>
      </div>
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div><div className={LABEL}>Creator sheet</div><p className="text-xs text-[#6B6B6B] -mt-1">Upload a CSV or add creators by hand. Total budget: <b className="text-[#FF451A]" data-testid="agency-plan-total">{inr(t.total)}</b></p></div>
          {editable && <UploadButton accept=".csv,text/csv" onFile={onCsv} busy={busy === 'csv'} label="Import CSV" icon={Upload} testid="agency-plan-csv-upload" />}
        </div>
        <CreatorSheetTable rows={form.rows} setRows={(rows) => setForm((s) => ({ ...s, rows }))} planFee={form.agency_fee} editable={editable} testid="agency-sheet" />
      </section>
      <section className="space-y-3">
        <div className="flex items-center justify-between"><div className={LABEL}>Pitch deck (PDF)</div>{editable && <UploadButton accept="application/pdf,.pdf" onFile={onDeck} busy={busy === 'deck'} label={plan?.pitch_deck ? 'Replace PDF' : 'Upload PDF'} icon={FileUp} testid="agency-plan-deck-upload" />}</div>
        {plan ? <PdfViewer planId={plan.id} deck={plan.pitch_deck} height={520} /> : <div className={`${CARD} p-6 text-sm text-[#6B6B6B]`}>Upload a PDF up to 25MB — the draft is saved automatically first.</div>}
      </section>
      {plan && <HistoryList history={plan.history} />}
    </PageShell>
  );
}
